/**
 * Wall-clock time in an IANA zone — the lowest layer every schedule reader
 * shares.
 *
 * Lives in `models` (not `common`) because the menu-schedule evaluators live
 * here and packages may only import downward. `@order-up/common`'s
 * `utils/timezone.ts` re-exports `isValidIanaTz` / `getZonedParts` from this
 * file, so there is still exactly one implementation.
 *
 * No `date-fns-tz` dependency: `Intl.DateTimeFormat` resolves the offset at a
 * specific instant in a specific zone, DST included.
 */

import type { WeekStartDay } from '../locations/Location';

const IANA_TZ_PATTERN = /^[A-Za-z_]+(?:\/[A-Za-z_+-][A-Za-z0-9_+-]*)*$/;

export function isValidIanaTz(tz: string): boolean {
  if (!tz || !IANA_TZ_PATTERN.test(tz)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Decompose a UTC instant into the wall-clock parts it renders as in `tz`.
 */
export function getZonedParts(
  instant: Date,
  timeZone: string,
): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(instant);
  const map: Record<string, number> = {};
  for (const part of parts) {
    if (part.type === 'literal') continue;
    map[part.type] = parseInt(part.value, 10);
  }
  // hour=24 happens at midnight on some locale paths — normalize to 0.
  if (map.hour === 24) map.hour = 0;
  return {
    year: map.year,
    month: map.month,
    day: map.day,
    hour: map.hour,
    minute: map.minute,
    second: map.second,
  };
}

/** A wall-clock reading, the only shape a schedule evaluator consumes. */
export interface IScheduleClock {
  readonly year: number;
  /** 1–12 */
  readonly month: number;
  /** 1–31 */
  readonly date: number;
  /** 0 = Sunday … 6 = Saturday, matching `MENU_GROUP_DAY_KEYS`. */
  readonly weekday: number;
  /** Minutes since midnight, 0–1439. */
  readonly minute: number;
}

/**
 * Read `now` on the RESTAURANT's clock.
 *
 * 🔴 WHY. A menu schedule is authored as restaurant wall-clock time ("Dinner
 * 6 PM–4 AM"), but every evaluator used to read the PROCESS clock: the till's
 * Android zone, the guest's browser, a UTC server. Any of those off the
 * restaurant's zone shifts every daypart by the offset — a UTC server shows a
 * New York dinner menu from 2 PM and pulls it at 8 PM.
 *
 * An absent or unrecognised zone falls back to the process clock — exactly the
 * old behaviour — rather than to UTC. A restaurant with no zone configured is no
 * worse off than before; guessing UTC would move every till that was right.
 */
export function readScheduleClock(now: Date, timeZone?: string | null): IScheduleClock {
  if (timeZone != null && isValidIanaTz(timeZone)) {
    const p = getZonedParts(now, timeZone);
    return {
      year: p.year,
      month: p.month,
      date: p.day,
      weekday: new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay(),
      minute: p.hour * 60 + p.minute,
    };
  }
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    date: now.getDate(),
    weekday: now.getDay(),
    minute: now.getHours() * 60 + now.getMinutes(),
  };
}

interface ZoneBearing {
  readonly settings?: { readonly timezone?: string | null } | null;
}

/**
 * The zone a restaurant's schedules are written in: the location's own
 * `settings.timezone`, then the tenant's. `undefined` when neither names a
 * valid IANA zone — which every evaluator reads as "keep the process clock".
 *
 * The same chain `useBusinessTimeZone`, the schedule service and the report
 * windows already use; this is the one both planes call for menu availability.
 */
export function resolveScheduleTimeZone(
  locationRecord?: ZoneBearing | null,
  tenant?: ZoneBearing | null,
): string | undefined {
  for (const candidate of [locationRecord?.settings?.timezone, tenant?.settings?.timezone]) {
    if (typeof candidate === 'string' && isValidIanaTz(candidate)) return candidate;
  }
  return undefined;
}

/**
 * Every day a business week can start on. A `Record` over the union, so a day
 * added to `WeekStartDay` without an entry here is a compile error.
 */
const WEEK_START_DAY_SET: Readonly<Record<WeekStartDay, true>> = {
  sunday: true,
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: true,
};

/** The seven `WeekStartDay` values, Sunday first. */
export const WEEK_START_DAYS: readonly WeekStartDay[] = Object.freeze(
  Object.keys(WEEK_START_DAY_SET) as WeekStartDay[],
);

interface WeekStartBearing {
  /** Any stored value: the resolver validates it. */
  readonly settings?: { readonly weekStartDay?: unknown } | null;
}

/**
 * The day a location's schedule week starts on: its own `settings.weekStartDay`,
 * else Monday. It is the source of the `weekStartDay` every schedule week the
 * server builds carries (`ScheduleService`), and `/users/me/locations` hands the
 * phones the same value so their roster weeks are the server's.
 *
 * The location's own setting only, as the schedule service has always read it.
 * A stored value that is not one of the seven days reads as Monday rather than
 * reaching the week math, which has no week for it.
 */
export function resolveScheduleWeekStartDay(locationRecord?: WeekStartBearing | null): WeekStartDay {
  const stored = locationRecord?.settings?.weekStartDay;
  return typeof stored === 'string' && Object.prototype.hasOwnProperty.call(WEEK_START_DAY_SET, stored)
    ? (stored as WeekStartDay)
    : 'monday';
}
